/**
 * Create home (UX plan §4.3 + §4.7, phase U2) — the default-first entry:
 * ONE prompt bar with a working default for everything else. Editable chips
 * expose the defaults (goal, style, angle count); the format matrix shows
 * angles x formats with a live total and an estimated cost; the «Customize
 * brief» accordion is the old 8-field dialog, now optional (D5). Recent
 * batches are the re-entry points (J6).
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Link,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CircleIcon from '@mui/icons-material/Circle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InsightsIcon from '@mui/icons-material/Insights';
import TuneIcon from '@mui/icons-material/Tune';
import { StyledTextField } from '@modules/ui-components';
import type {
  AdCreativeBrief,
  AdCreativeDesignCollection,
  AdCreativeItem,
  AdCreativeTemplate,
} from '@segmently/schema-models/ad-creative';
import { AD_CREATIVE_BATCH, AD_CREATIVE_BRIEFS, AD_CREATIVE_FORMATS, AD_CREATIVE_FRAMEWORKS, AD_CREATIVE_INSIGHT } from '../../selectors';
import { GenerationSettingsPopover } from '../common/GenerationSettingsPopover';
import { useAdCreativeGenSettings } from '../../hooks/useAdCreativeGenSettings';
import type { AdCreativeApiClient, AdCreativeFrameworkOption } from '../../services/AdCreativeApiClient';
import {
  AdCreativeInsightContextClient,
  INSIGHT_CATEGORY_COLOR,
  type AdCreativeAnalysisOption,
  type AdCreativeDerivedChips,
  type AdCreativeInsightIndex,
} from '../../services/AdCreativeInsightContextClient';
import { loadProofSourceOptions, type AdCreativeProofOption } from '../../services/adCreativeProofRefs';
import { DataReferencePickerDialog } from '@modules/ui-components/components/DataReferencePickerDialog/DataReferencePickerDialog';
import type { DataReferenceSelectionResult } from '@modules/ui-components/components/DataReferencePickerDialog/types';
import type { PickerConfig } from '@modules/data-pickers';
import { deriveCardStatus, getBriefCampaignId } from '../batch/batchModel';

/** Manual thesis subset threaded into generate-batch (FR3 contract). */
export interface InsightSelectionState {
  mode: 'auto' | 'manual';
  ids: string[];
}

/**
 * Context-dialog tabs — the SAME composite mechanism strategy generation uses
 * (StrategyFormDialog/BlockFormDialog). Selection happens ONLY here; the
 * context row is display-only.
 */
const CONTEXT_PICKERS: PickerConfig[] = [
  { type: 'insights', label: 'Insights', enabled: true },
  { type: 'audience', label: 'Audiences', enabled: true },
  { type: 'task', label: 'Tasks', enabled: true },
  { type: 'socialproof', label: 'Social proof', enabled: true },
];

/** Entity refs picked in the context dialog — merged into request.sourceRefs. */
interface ContextEntityRefs {
  audienceIds: string[];
  taskIds: string[];
  reviewIds: string[];
  bigNumberIds: string[];
  ratingIds: string[];
  studyCaseIds: string[];
}

const EMPTY_ENTITY_REFS: ContextEntityRefs = {
  audienceIds: [], taskIds: [], reviewIds: [], bigNumberIds: [], ratingIds: [], studyCaseIds: [],
};

function contextPathsStorageKey(projectId: string): string {
  return `adCreative.contextPaths.${projectId}`;
}

/** Entity paths (@audience[id]…, @review[id]…) → grouped unique ids. */
function parseEntityRefs(paths: string[]): ContextEntityRefs {
  const refs: ContextEntityRefs = {
    audienceIds: [], taskIds: [], reviewIds: [], bigNumberIds: [], ratingIds: [], studyCaseIds: [],
  };
  const buckets: Array<[RegExp, keyof ContextEntityRefs]> = [
    [/^@audience\[([^\]]+)\]/, 'audienceIds'],
    [/^@task\[([^\]]+)\]/, 'taskIds'],
    [/^@review\[([^\]]+)\]/, 'reviewIds'],
    [/^@bigNumber\[([^\]]+)\]/, 'bigNumberIds'],
    [/^@rating\[([^\]]+)\]/, 'ratingIds'],
    [/^@studyCase\[([^\]]+)\]/, 'studyCaseIds'],
  ];
  for (const path of paths) {
    for (const [pattern, bucket] of buckets) {
      const match = pattern.exec(path);
      if (match && !refs[bucket].includes(match[1])) refs[bucket].push(match[1]);
    }
  }
  return refs;
}

/** FR3: per-project subset persistence until the batch stamps it on the brief. */
function selectionStorageKey(projectId: string): string {
  return `adCreative.insightSelection.${projectId}`;
}

function readStoredSelection(projectId: string, analysisId: string): InsightSelectionState {
  try {
    const raw = localStorage.getItem(selectionStorageKey(projectId));
    if (!raw) return { mode: 'auto', ids: [] };
    const parsed = JSON.parse(raw) as { analysisId?: string; mode?: string; ids?: unknown };
    if (parsed.analysisId !== analysisId || parsed.mode !== 'manual' || !Array.isArray(parsed.ids)) {
      return { mode: 'auto', ids: [] };
    }
    return { mode: 'manual', ids: parsed.ids.map(String).filter(Boolean) };
  } catch {
    return { mode: 'auto', ids: [] };
  }
}

export const HOME = {
  root: 'ad-creative-home',
  promptBar: 'ad-creative-home-prompt',
  generate: 'ad-creative-home-generate',
  chip: (key: string) => `ad-creative-home-chip-${key}`,
  formatCard: (key: string) => `ad-creative-home-format-${key}`,
  matrixTotal: 'ad-creative-home-matrix-total',
  customizeAccordion: 'ad-creative-home-customize',
  recentBatches: 'ad-creative-home-recent',
  recentBatch: (id: string) => `ad-creative-home-recent-${id}`,
  link: (key: string) => `ad-creative-home-link-${key}`,
} as const;

/**
 * FR4: formats come from getCatalog(networkId) — the hardcoded 4-template
 * constant is gone. This map exists ONLY to migrate legacy saved defaults
 * (feed|story|reel|carousel keys) to their template ids transparently.
 */
const LEGACY_FORMAT_TO_TEMPLATE: Record<string, string> = {
  feed: 'meta_feed_single_image_proof_hook',
  story: 'meta_story_static_offer',
  reel: 'meta_reel_creator_native_hook',
  carousel: 'meta_carousel_narrative_cards',
};

const DEFAULT_TEMPLATE_IDS = [
  LEGACY_FORMAT_TO_TEMPLATE.feed,
  LEGACY_FORMAT_TO_TEMPLATE.story,
  LEGACY_FORMAT_TO_TEMPLATE.reel,
];

/** Family of a catalog template (served field; id-prefix fallback). */
function templateFamily(template: AdCreativeTemplate): string {
  const served = (template as unknown as { formatFamily?: string }).formatFamily;
  if (served) return served;
  const match = /^[a-z0-9]+_([a-z0-9]+(?:_[a-z0-9]+)?)_/.exec(String(template.id));
  return match?.[1] ?? 'other';
}

/** Short media tag for a template row (image/video/carousel/…). */
function templateMediaTag(template: AdCreativeTemplate): string {
  const type = String(template.creativeType ?? '');
  if (type.includes('video') || type === 'reel') return 'video';
  if (type === 'carousel') return 'carousel';
  if (type === 'collection' || type === 'catalog_product') return 'product set';
  if (type === 'instant_experience') return 'fullscreen';
  return 'image';
}

const GOAL_OPTIONS = [
  { id: 'leads', label: 'Leads' },
  { id: 'sales', label: 'Sales' },
  { id: 'awareness', label: 'Awareness' },
  { id: 'traffic', label: 'Traffic' },
] as const;

/** Objective → recommended format FAMILIES with the reason shown inline (§4.7). */
const RECOMMENDATIONS: Record<string, { families: string[]; reason: string }> = {
  awareness: { families: ['reel', 'story'], reason: 'video-first for cold reach' },
  leads: { families: ['feed', 'story'], reason: 'proof statics convert intent' },
  sales: { families: ['feed', 'story'], reason: 'statics for retargeting' },
  traffic: { families: ['feed', 'reel'], reason: 'hook + click' },
};

/** Rough per-item generation cost (image + copy calls), always labeled estimated. */
const ESTIMATED_USD_PER_ITEM = 0.55;

interface AdCreativeCreateHomeProps {
  projectId: string;
  client: AdCreativeApiClient;
  briefs: AdCreativeBrief[];
  items: AdCreativeItem[];
  designCollections: AdCreativeDesignCollection[];
  onOpenBatch: (briefId: string) => void;
  onOpenView: (view: 'brand' | 'library' | 'import') => void;
  onRefresh: () => void;
  /** Navigate to the project Insides tab (ungrounded-state escape hatch). */
  onOpenInsights?: () => void;
}

export function AdCreativeCreateHome({
  projectId,
  client,
  briefs,
  items,
  designCollections,
  onOpenBatch,
  onOpenView,
  onRefresh,
  onOpenInsights,
}: AdCreativeCreateHomeProps): React.ReactElement {
  const [promptText, setPromptText] = useState('');
  const [goal, setGoal] = useState<string>('leads');
  const [angleCount, setAngleCount] = useState(3);
  const [styleId, setStyleId] = useState('');
  // FR4: catalog-driven network + template selection (mock 03 left).
  const [networkId, setNetworkId] = useState('meta');
  const [networks, setNetworks] = useState<Array<{ id: string; displayName?: string; templateCount?: number }>>([]);
  const [catalogTemplates, setCatalogTemplates] = useState<AdCreativeTemplate[]>([]);
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<string[]>(DEFAULT_TEMPLATE_IDS);
  const [networkMenuAnchor, setNetworkMenuAnchor] = useState<HTMLElement | null>(null);
  const [goalMenuAnchor, setGoalMenuAnchor] = useState<HTMLElement | null>(null);
  const [styleMenuAnchor, setStyleMenuAnchor] = useState<HTMLElement | null>(null);
  const [countMenuAnchor, setCountMenuAnchor] = useState<HTMLElement | null>(null);
  const [audienceName, setAudienceName] = useState('');
  const [painOrNeed, setPainOrNeed] = useState('');
  // Insight-grounded context row (mock 01): pinned analysis + derived chips.
  const [analyses, setAnalyses] = useState<AdCreativeAnalysisOption[] | null>(null);
  const [analysisId, setAnalysisId] = useState('');
  const [derivedChips, setDerivedChips] = useState<AdCreativeDerivedChips>({});
  const [jobToBeDone, setJobToBeDone] = useState('');
  const [analysisMenuAnchor, setAnalysisMenuAnchor] = useState<HTMLElement | null>(null);
  const [offerPromise, setOfferPromise] = useState('');
  const [proofSummary, setProofSummary] = useState('');
  const [cta, setCta] = useState('');
  const [destinationUrl, setDestinationUrl] = useState('');
  const [instruction, setInstruction] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [metaConnected, setMetaConnected] = useState<boolean | null>(null);
  const [providerSourceCount, setProviderSourceCount] = useState<number | null>(null);
  // FR3: thesis subset (mock 01), «from insights» field menus + proof picker
  // (mock 02). All hidden when the project is ungrounded.
  const [insightIndex, setInsightIndex] = useState<AdCreativeInsightIndex>({});
  const [selection, setSelection] = useState<InsightSelectionState>({ mode: 'auto', ids: [] });
  const [audienceOptions, setAudienceOptions] = useState<string[]>([]);
  const [fieldMenu, setFieldMenu] = useState<{ field: 'audience' | 'pain' | 'job'; anchor: HTMLElement } | null>(null);
  const [proofOptions, setProofOptions] = useState<AdCreativeProofOption[]>([]);
  const [proofRefs, setProofRefs] = useState<AdCreativeProofOption[]>([]);
  const [proofMenuAnchor, setProofMenuAnchor] = useState<HTMLElement | null>(null);
  // Context dialog (strategy mechanism): full path selection; the row displays.
  const [contextDialogOpen, setContextDialogOpen] = useState(false);
  const [contextPaths, setContextPaths] = useState<string[]>([]);
  const [entityRefs, setEntityRefs] = useState<ContextEntityRefs>(EMPTY_ENTITY_REFS);
  // FR7: creative frameworks (per format family) + the peer custom description.
  const [frameworks, setFrameworks] = useState<AdCreativeFrameworkOption[]>([]);
  const [frameworkIds, setFrameworkIds] = useState<string[]>([]);
  const [customBrief, setCustomBrief] = useState('');
  const [frameworkMenu, setFrameworkMenu] = useState<{ family: string; anchor: HTMLElement } | null>(null);

  useEffect(() => {
    let cancelled = false;
    void client.getMetaAdsStatus(projectId).then((status) => {
      if (!cancelled) setMetaConnected(Boolean(status.connected && !status.needsReconnect));
    }).catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [client, projectId]);

  useEffect(() => {
    if (typeof client.listProviderCreativeSources !== 'function') return;
    let cancelled = false;
    void client.listProviderCreativeSources(projectId, { limit: 100 }).then((response) => {
      if (!cancelled && response.success && response.data) {
        setProviderSourceCount(response.data.sources.length);
      }
    }).catch(() => {
      if (!cancelled) setProviderSourceCount(null);
    });
    return () => {
      cancelled = true;
    };
  }, [client, projectId]);

  const insightClient = useMemo(() => new AdCreativeInsightContextClient(projectId), [projectId]);
  // FR4: per-project generation settings (model/effort/budget).
  const genSettings = useAdCreativeGenSettings(projectId);

  useEffect(() => {
    // Pin defaults to the latest completed analysis — the same fallback rule
    // the backend applies; the select just makes the pin visible/editable.
    let cancelled = false;
    void insightClient.listCompletedAnalyses().then((options) => {
      if (cancelled) return;
      setAnalyses(options);
      setAnalysisId((current) => current || options[0]?.id || '');
    }).catch(() => {
      if (!cancelled) setAnalyses([]);
    });
    return () => {
      cancelled = true;
    };
  }, [insightClient]);

  // Manual thesis subset scopes the derived chips too (fix: unselected
  // analysis content must not leak into the batch context via the chips).
  const manualSelectionKey = selection.mode === 'manual' ? selection.ids.join(',') : '';

  useEffect(() => {
    // Chip pre-fill (D3): silently pre-filled with a provenance dot; edits in
    // the Customize accordion flip a chip to "manual" and always win. With a
    // manual selection the derivation uses ONLY the selected theses.
    if (!analysisId) {
      setDerivedChips({});
      return;
    }
    let cancelled = false;
    void insightClient.deriveChips(analysisId, manualSelectionKey ? manualSelectionKey.split(',') : undefined)
      .then((chips) => {
        if (!cancelled) setDerivedChips(chips);
      }).catch(() => {
        if (!cancelled) setDerivedChips({});
      });
    return () => {
      cancelled = true;
    };
  }, [insightClient, analysisId, manualSelectionKey]);

  useEffect(() => {
    // FR3: full thesis index (subset picker + field menus) + audience options
    // for the SELECTED analysis only; restore the per-project subset.
    if (!analysisId) {
      setInsightIndex({});
      setAudienceOptions([]);
      setSelection({ mode: 'auto', ids: [] });
      return;
    }
    setSelection(readStoredSelection(projectId, analysisId));
    let cancelled = false;
    void insightClient.loadInsightIndex(analysisId).then((index) => {
      if (!cancelled) setInsightIndex(index);
    }).catch(() => {
      if (!cancelled) setInsightIndex({});
    });
    void insightClient.listAudienceOptions(analysisId).then((options) => {
      if (!cancelled) setAudienceOptions(options);
    }).catch(() => {
      if (!cancelled) setAudienceOptions([]);
    });
    return () => {
      cancelled = true;
    };
  }, [insightClient, analysisId, projectId]);

  useEffect(() => {
    // FR3: pickable proof sources (reviews/bigNumbers) — one-shot load.
    let cancelled = false;
    void loadProofSourceOptions(projectId).then((options) => {
      if (!cancelled) setProofOptions(options);
    }).catch(() => {
      if (!cancelled) setProofOptions([]);
    });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const applySelection = (next: InsightSelectionState) => {
    setSelection(next);
    try {
      if (next.mode === 'manual' && next.ids.length > 0) {
        localStorage.setItem(selectionStorageKey(projectId), JSON.stringify({ analysisId, ...next }));
      } else {
        localStorage.removeItem(selectionStorageKey(projectId));
      }
    } catch {
      // storage unavailable — selection still applies for this session
    }
  };

  useEffect(() => {
    // Restore dialog-picked context paths (per project). Insight categories
    // were expanded to item paths at Apply time — no hydration needed here.
    try {
      const raw = localStorage.getItem(contextPathsStorageKey(projectId));
      if (!raw) return;
      const parsed = JSON.parse(raw) as unknown;
      if (Array.isArray(parsed)) {
        const paths = parsed.map(String).filter(Boolean);
        setContextPaths(paths);
        setEntityRefs(parseEntityRefs(paths));
      }
    } catch {
      // storage unavailable — the dialog still works for this session
    }
  }, [projectId]);

  /**
   * Context dialog Apply — the ONLY place the generation context is edited
   * (strategy mechanism). Maps picked paths onto the existing request fields:
   * insight items → manual subset (+ pin), entities → sourceRefs, audience →
   * display name on the derived chip.
   */
  const applyContextResult = (result: DataReferenceSelectionResult) => {
    // Whole-category insight picks expand into item paths NOW — the hydrated
    // analyses exist only at Apply time.
    const expanded: string[] = [];
    for (const path of result.paths) {
      const category = /^@insight\[([^\]]+)\]\.([^[.]+)$/.exec(path);
      if (!category) {
        expanded.push(path);
        continue;
      }
      const analysis = result.hydratedData.insights?.analyses.find((entry) => entry.id === category[1]);
      const items = analysis?.aggregatedInsights?.[category[2]] ?? [];
      if (items.length === 0) expanded.push(path);
      else for (const item of items) expanded.push(`@insight[${category[1]}].${category[2]}[${item.id}]`);
    }
    const paths = Array.from(new Set(expanded));
    setContextPaths(paths);
    try {
      if (paths.length > 0) localStorage.setItem(contextPathsStorageKey(projectId), JSON.stringify(paths));
      else localStorage.removeItem(contextPathsStorageKey(projectId));
    } catch {
      // storage unavailable — selection still applies for this session
    }

    // Insight items → manual subset; the pin follows the picked analysis.
    const itemMatches = paths
      .map((path) => /^@insight\[([^\]]+)\]\.([^[.]+)\[([^\]]+)\]$/.exec(path))
      .filter((match): match is RegExpExecArray => match !== null);
    const pinnedId = itemMatches[0]?.[1] ?? analysisId;
    const ids = Array.from(new Set(itemMatches.filter((match) => match[1] === pinnedId).map((match) => match[3])));
    setSelection(ids.length > 0 ? { mode: 'manual', ids } : { mode: 'auto', ids: [] });
    try {
      if (ids.length > 0) {
        // Written with the PICKED analysis id: switching the pin re-reads this.
        localStorage.setItem(selectionStorageKey(projectId), JSON.stringify({ analysisId: pinnedId, mode: 'manual', ids }));
      } else {
        localStorage.removeItem(selectionStorageKey(projectId));
      }
    } catch {
      // storage unavailable — selection still applies for this session
    }
    if (pinnedId && pinnedId !== analysisId) setAnalysisId(pinnedId);

    // Entities → sourceRefs; the first picked audience names the chip.
    setEntityRefs(parseEntityRefs(paths));
    const audienceEntity = result.hydratedData.audiences?.[0];
    const audienceLabel = typeof audienceEntity?.name === 'string' && audienceEntity.name.trim()
      ? audienceEntity.name.trim()
      : undefined;
    if (audienceLabel) {
      setDerivedChips((current) => ({ ...current, audienceName: audienceLabel }));
      setAudienceName('');
    }
  };

  const defaultCollection = useMemo(
    () => designCollections.find((collection) => collection.isCurrent || collection.isDefault) ?? designCollections[0] ?? null,
    [designCollections],
  );

  useEffect(() => {
    setStyleId((current) => current || defaultCollection?.id || '');
  }, [defaultCollection?.id]);

  useEffect(() => {
    // FR4: networks + the selected network's full catalog.
    let cancelled = false;
    void client.listNetworks().then((response) => {
      if (cancelled) return;
      const list = response.data?.networks ?? [];
      if (list.length) setNetworks(list.map((network) => ({ id: network.id, displayName: network.displayName })));
    }).catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [client]);

  useEffect(() => {
    let cancelled = false;
    void client.getCatalog(networkId).then((response) => {
      if (cancelled) return;
      const templates = response.data?.templates ?? [];
      setCatalogTemplates(templates);
      // FR7: curated frameworks ride the same catalog response.
      setFrameworks(response.data?.frameworks ?? []);
      // Keep only selections that exist in this network's catalog.
      setSelectedTemplateIds((current) => {
        const valid = current.filter((id) => templates.some((template) => template.id === id));
        return valid.length > 0 ? valid : templates.slice(0, 3).map((template) => template.id);
      });
    }).catch(() => {
      if (!cancelled) setCatalogTemplates([]);
    });
    return () => {
      cancelled = true;
    };
  }, [client, networkId]);

  useEffect(() => {
    // Remembered per-project defaults (L1 chips). Missing settings are fine.
    // FR4-AC-008: legacy saved format keys migrate to template ids once.
    let cancelled = false;
    void client.getPlatformSettings(projectId).then((response) => {
      if (cancelled || !response.data?.settings?.defaults) return;
      const defaults = response.data.settings.defaults as Record<string, unknown>;
      if (defaults.goal) setGoal(String(defaults.goal));
      if (defaults.countPerFormat) setAngleCount(Number(defaults.countPerFormat));
      if (defaults.designCollectionId) setStyleId(String(defaults.designCollectionId));
      const byNetwork = defaults.templateIdsByNetwork as Record<string, unknown> | undefined;
      const savedForNetwork = byNetwork && Array.isArray(byNetwork[networkId])
        ? (byNetwork[networkId] as unknown[]).map(String).filter(Boolean)
        : null;
      // FR7: per-network framework defaults (same pattern as templates).
      const frameworksByNetwork = defaults.frameworkIdsByNetwork as Record<string, unknown> | undefined;
      if (frameworksByNetwork && Array.isArray(frameworksByNetwork[networkId])) {
        setFrameworkIds((frameworksByNetwork[networkId] as unknown[]).map(String).filter(Boolean));
      }
      if (savedForNetwork && savedForNetwork.length) {
        setSelectedTemplateIds(savedForNetwork);
      } else if (Array.isArray(defaults.formats) && defaults.formats.length) {
        const migrated = (defaults.formats as unknown[])
          .map((key) => LEGACY_FORMAT_TO_TEMPLATE[String(key)])
          .filter(Boolean);
        if (migrated.length) setSelectedTemplateIds(migrated);
      }
    }).catch(() => undefined);
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, projectId]);

  const selectedAnalysis = useMemo(
    () => (analyses ?? []).find((option) => option.id === analysisId) ?? null,
    [analyses, analysisId],
  );
  // A chip is "manual" once the user typed a value (Customize accordion or
  // chip edit); empty user value -> the derived preview with an info dot.
  const effectiveAudience = audienceName || derivedChips.audienceName || '';
  const effectivePain = painOrNeed || derivedChips.painOrNeed || '';
  const effectiveJob = jobToBeDone || derivedChips.jobToBeDone || '';
  // Entities picked in the context dialog (display + sourceRefs).
  const contextSourceCount = entityRefs.audienceIds.length + entityRefs.taskIds.length
    + entityRefs.reviewIds.length + entityRefs.bigNumberIds.length
    + entityRefs.ratingIds.length + entityRefs.studyCaseIds.length;
  // A manual thesis selection (or grounded derived chips) is a complete
  // generation context by itself — the free-text prompt is then OPTIONAL:
  // creatives form from the selected insights, no extra typing required.
  const hasInsightContext = (selection.mode === 'manual' && selection.ids.length > 0)
    || Boolean(effectiveAudience || effectivePain || effectiveJob);
  const grounded = Boolean(selectedAnalysis);

  const rederiveChips = () => {
    setAudienceName('');
    setPainOrNeed('');
    setJobToBeDone('');
  };

  const recommendation = RECOMMENDATIONS[goal] ?? RECOMMENDATIONS.leads;
  const totalCreatives = angleCount * selectedTemplateIds.length;
  const estimatedCost = (totalCreatives * ESTIMATED_USD_PER_ITEM).toFixed(2);
  const styleName = designCollections.find((collection) => collection.id === styleId)?.name
    ?? (styleId ? styleId : 'Auto');

  const recentBriefs = useMemo(() => briefs.slice(0, 6), [briefs]);

  // FR4: family groups of the ACTIVE network's catalog.
  const templateFamilies = useMemo(() => {
    const byFamily = new Map<string, AdCreativeTemplate[]>();
    for (const template of catalogTemplates) {
      const family = templateFamily(template);
      const list = byFamily.get(family) ?? [];
      list.push(template);
      byFamily.set(family, list);
    }
    return Array.from(byFamily.entries());
  }, [catalogTemplates]);

  const toggleTemplate = (templateId: string) => {
    setSelectedTemplateIds((current) => (current.includes(templateId)
      ? current.filter((entry) => entry !== templateId)
      : [...current, templateId]));
  };

  const toggleFamily = (family: string) => {
    const familyIds = (templateFamilies.find(([name]) => name === family)?.[1] ?? []).map((template) => template.id);
    setSelectedTemplateIds((current) => {
      const allSelected = familyIds.every((id) => current.includes(id));
      return allSelected
        ? current.filter((id) => !familyIds.includes(id))
        : Array.from(new Set([...current, ...familyIds]));
    });
  };

  // FR7: JTBD categories present in the CURRENT insight selection — colors the
  // affinity chips so the user sees which frameworks their selection fuels.
  const selectedCategories = useMemo(() => {
    const categories = new Set<string>();
    if (selection.mode === 'manual' && selection.ids.length > 0) {
      for (const id of selection.ids) {
        const entry = insightIndex[id];
        if (entry) categories.add(entry.category);
      }
    } else {
      for (const entry of Object.values(insightIndex)) categories.add(entry.category);
    }
    return categories;
  }, [selection, insightIndex]);

  const frameworkById = useMemo(
    () => new Map(frameworks.map((framework) => [framework.id, framework])),
    [frameworks],
  );
  const frameworksByFamily = useMemo(() => {
    const byFamily = new Map<string, AdCreativeFrameworkOption[]>();
    const score = (framework: AdCreativeFrameworkOption) => Object.entries(framework.insightAffinities ?? {})
      .reduce((sum, [category, weight]) => sum + (selectedCategories.has(category) ? Number(weight) : 0), 0);
    for (const framework of frameworks) {
      for (const family of framework.formatFamilies) {
        const list = byFamily.get(family) ?? [];
        list.push(framework);
        byFamily.set(family, list);
      }
    }
    for (const list of byFamily.values()) {
      list.sort((left, right) => score(right) - score(left) || left.name.localeCompare(right.name));
    }
    return byFamily;
  }, [frameworks, selectedCategories]);

  /** ≤2 per kind; picking a framework clears the custom description (explicit peer choice). */
  const toggleFramework = (frameworkId: string) => {
    const framework = frameworkById.get(frameworkId);
    if (!framework) return;
    setFrameworkIds((current) => {
      if (current.includes(frameworkId)) return current.filter((id) => id !== frameworkId);
      const sameKind = current.filter((id) => frameworkById.get(id)?.kind === framework.kind);
      const next = sameKind.length >= 2
        ? current.filter((id) => id !== sameKind[0])
        : current;
      return [...next, frameworkId];
    });
    setCustomBrief('');
  };

  const submit = async (mode: 'direct' | 'briefs_first' = 'direct') => {
    if ((!promptText.trim() && !hasInsightContext) || selectedTemplateIds.length === 0) return;
    setSubmitting(true);
    setError(null);
    try {
      const templateIds = selectedTemplateIds;
      const response = await client.generateBatch({
        projectId,
        promptText: promptText.trim() || undefined,
        templateIds,
        networkId,
        // FR8: briefs_first stops after draft briefs — review before rendering.
        ...(mode === 'briefs_first' ? { mode } : {}),
        generation: genSettings.requestPayload,
        objective: goal,
        angleCount,
        designCollectionId: styleId || undefined,
        insightsAnalysisId: analysisId || undefined,
        // FR3: manual subset stamps onto the brief; auto sends nothing.
        ...(selection.mode === 'manual' && selection.ids.length > 0
          ? { selectedInsightIds: selection.ids, insightSelectionMode: 'manual' as const }
          : {}),
        // Picked sources make @review/@bigNumber citations resolvable and
        // stamp audience/task refs — union of the context dialog and the
        // Customize-brief proof menu.
        ...((() => {
          const reviewIds = Array.from(new Set([
            ...proofRefs.filter((ref) => ref.kind === 'review').map((ref) => ref.id),
            ...entityRefs.reviewIds,
          ]));
          const bigNumberIds = Array.from(new Set([
            ...proofRefs.filter((ref) => ref.kind === 'bigNumber').map((ref) => ref.id),
            ...entityRefs.bigNumberIds,
          ]));
          const sourceRefs = {
            ...(entityRefs.audienceIds.length > 0 ? { audienceIds: entityRefs.audienceIds } : {}),
            ...(entityRefs.taskIds.length > 0 ? { taskIds: entityRefs.taskIds } : {}),
            ...(reviewIds.length > 0 ? { reviewIds } : {}),
            ...(bigNumberIds.length > 0 ? { bigNumberIds } : {}),
            ...(entityRefs.ratingIds.length > 0 ? { ratingIds: entityRefs.ratingIds } : {}),
            ...(entityRefs.studyCaseIds.length > 0 ? { studyCaseIds: entityRefs.studyCaseIds } : {}),
          };
          return Object.keys(sourceRefs).length > 0 ? { sourceRefs } : {};
        })()),
        // FR7: frameworks OR the custom description (mutually exclusive in UI).
        ...(frameworkIds.length > 0 ? { frameworkIds } : {}),
        ...(customBrief.trim() ? { customCreativeBrief: customBrief.trim() } : {}),
        audienceName: effectiveAudience || undefined,
        painOrNeed: effectivePain || undefined,
        jobToBeDone: effectiveJob || undefined,
        offerPromise: offerPromise || undefined,
        proofSummary: proofSummary || undefined,
        cta: cta || undefined,
        destinationUrl: destinationUrl || undefined,
        userInstruction: instruction || undefined,
      });
      if (!response.success || !response.data?.brief) {
        throw new Error(response.error || 'Failed to start the batch');
      }
      // Remember the chips for next time (fire-and-forget). FR4: template ids
      // persist per network (legacy `formats` keys stop being written).
      void client.updatePlatformSettings(projectId, {
        defaults: {
          goal,
          countPerFormat: angleCount,
          designCollectionId: styleId || undefined,
          templateIdsByNetwork: { [networkId]: selectedTemplateIds },
          // FR7: framework defaults survive reload per network.
          frameworkIdsByNetwork: { [networkId]: frameworkIds },
        } as Record<string, unknown>,
      } as never).catch(() => undefined);
      onRefresh();
      onOpenBatch(response.data.brief.id);
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : String(nextError));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Stack spacing={3} data-testid={HOME.root}>
      <Box sx={{ maxWidth: 860, mx: 'auto', width: '100%', pt: { xs: 1, md: 4 } }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>Create ad creatives</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          One sentence is enough — defaults handle the rest.
        </Typography>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-start' }}>
          <StyledTextField
            placeholder="What are we advertising? e.g. YAZIO calorie tracker — free trial for busy parents"
            fullWidth
            multiline
            minRows={1}
            value={promptText}
            data-testid={HOME.promptBar}
            onChange={(value) => setPromptText(String(value))}
          />
          <Button
            variant="contained"
            size="large"
            startIcon={submitting ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
            data-testid={HOME.generate}
            disabled={submitting || (!promptText.trim() && !hasInsightContext) || selectedTemplateIds.length === 0}
            onClick={() => void submit()}
            sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            Generate batch
          </Button>
          {/* FR8: review draft briefs before any rendering (text-only step). */}
          <Button
            variant="outlined"
            size="large"
            data-testid={AD_CREATIVE_BRIEFS.briefsFirstButton}
            disabled={submitting || (!promptText.trim() && !hasInsightContext) || selectedTemplateIds.length === 0}
            onClick={() => void submit('briefs_first')}
            sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            Briefs first
          </Button>
        </Stack>

        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
          <Chip
            size="small"
            data-testid={HOME.chip('goal')}
            label={`Goal: ${GOAL_OPTIONS.find((option) => option.id === goal)?.label ?? goal}`}
            onClick={(event) => setGoalMenuAnchor(event.currentTarget)}
          />
          <Chip
            size="small"
            data-testid={HOME.chip('count')}
            label={`Angles: ${angleCount}`}
            onClick={(event) => setCountMenuAnchor(event.currentTarget)}
          />
          <Chip
            size="small"
            data-testid={HOME.chip('style')}
            label={`Style: ${styleName}`}
            onClick={(event) => setStyleMenuAnchor(event.currentTarget)}
          />
        </Stack>

        {/* Context row (mock 01): pinned analysis + derived chips with provenance dots. */}
        {grounded && (
          <Stack
            direction="row"
            spacing={1}
            sx={{ mt: 1 }}
            flexWrap="wrap"
            useFlexGap
            alignItems="center"
            data-testid={AD_CREATIVE_INSIGHT.contextRow}
          >
            <Typography variant="caption" color="text.secondary">Context:</Typography>
            <Chip
              size="small"
              icon={<InsightsIcon sx={{ fontSize: 14 }} />}
              data-testid={AD_CREATIVE_INSIGHT.analysisSelect}
              label={`Insights: ${selectedAnalysis!.label}${selectedAnalysis!.insightCount ? ` · ${selectedAnalysis!.insightCount} insights` : ''}`}
              onClick={(event) => setAnalysisMenuAnchor(event.currentTarget)}
            />
            {/* Display-only summary — selection happens in the context dialog. */}
            <Chip
              size="small"
              variant="outlined"
              data-testid={AD_CREATIVE_INSIGHT.subsetChip}
              data-selection-mode={selection.mode}
              label={selection.mode === 'manual'
                ? `🧩 Used: ${selection.ids.length} selected`
                : '🧩 Used: Auto top-8'}
            />
            {contextSourceCount > 0 && (
              <Chip
                size="small"
                variant="outlined"
                data-testid={AD_CREATIVE_INSIGHT.contextSources}
                label={`Sources: ${contextSourceCount}`}
              />
            )}
            <Chip
              size="small"
              color="primary"
              variant="outlined"
              icon={<TuneIcon sx={{ fontSize: 14 }} />}
              data-testid={AD_CREATIVE_INSIGHT.contextConfigure}
              label="Configure"
              onClick={() => setContextDialogOpen(true)}
            />
            {([
              { key: 'audience', prefix: 'Audience', value: effectiveAudience, manual: Boolean(audienceName) },
              { key: 'pain', prefix: 'Pain', value: effectivePain, manual: Boolean(painOrNeed) },
              { key: 'job', prefix: 'Job', value: effectiveJob, manual: Boolean(jobToBeDone) },
            ] as const).filter((chip) => chip.value).map((chip) => (
              <Chip
                key={chip.key}
                size="small"
                variant="outlined"
                data-testid={AD_CREATIVE_INSIGHT.chip(chip.key)}
                data-provenance={chip.manual ? 'manual' : 'derived'}
                icon={(
                  <CircleIcon sx={{ fontSize: 8, color: chip.manual ? 'text.disabled' : 'info.main' }} />
                )}
                label={`${chip.prefix}: ${chip.value.length > 42 ? `${chip.value.slice(0, 42)}…` : chip.value}`}
              />
            ))}
            <Typography variant="caption" color="text.secondary" data-testid={AD_CREATIVE_INSIGHT.legend}>
              <CircleIcon sx={{ fontSize: 8, color: 'info.main', mr: 0.25, verticalAlign: 'middle' }} />
              derived from insights ·
              <CircleIcon sx={{ fontSize: 8, color: 'text.disabled', mx: 0.25, verticalAlign: 'middle' }} />
              manual
            </Typography>
            <Link
              component="button"
              variant="caption"
              data-testid={AD_CREATIVE_INSIGHT.rederive}
              onClick={rederiveChips}
            >
              ↻ Re-derive
            </Link>
          </Stack>
        )}
        {analyses !== null && analyses.length === 0 && (
          <Alert
            severity="warning"
            sx={{ mt: 1 }}
            data-testid={AD_CREATIVE_INSIGHT.ungroundedStrip}
            action={(
              <Link
                component="button"
                variant="caption"
                data-testid={AD_CREATIVE_INSIGHT.ungroundedLink}
                onClick={() => onOpenInsights?.()}
                sx={{ whiteSpace: 'nowrap' }}
              >
                Run insights analysis ↗
              </Link>
            )}
          >
            No completed insights analysis — ads will be generated <b>ungrounded</b> (template + prompt only).
          </Alert>
        )}
        {/* Context dialog — the strategy CompositeDataPicker mechanism: ALL
            selection lives here; the row above only displays the result. */}
        <DataReferencePickerDialog
          open={contextDialogOpen}
          onClose={() => setContextDialogOpen(false)}
          onApply={applyContextResult}
          projectId={projectId}
          initialSelection={contextPaths}
          title="Configure generation context"
          pickers={CONTEXT_PICKERS}
          allowEmptyApply
          height={420}
          maxWidth="md"
          data-testid={AD_CREATIVE_INSIGHT.contextDialog}
        />
        <Menu anchorEl={analysisMenuAnchor} open={Boolean(analysisMenuAnchor)} onClose={() => setAnalysisMenuAnchor(null)}>
          {(analyses ?? []).map((option) => (
            <MenuItem
              key={option.id}
              selected={option.id === analysisId}
              data-testid={AD_CREATIVE_INSIGHT.analysisOption(option.id)}
              onClick={() => {
                setAnalysisId(option.id);
                setAnalysisMenuAnchor(null);
              }}
            >
              {`${option.label}${option.insightCount ? ` · ${option.insightCount} insights` : ''}`}
            </MenuItem>
          ))}
        </Menu>
        {/* FR3: «from insights ▾» pickers — picked values stay provenance-derived. */}
        <Menu
          anchorEl={fieldMenu?.anchor ?? null}
          open={Boolean(fieldMenu)}
          onClose={() => setFieldMenu(null)}
          slotProps={{ paper: { sx: { maxWidth: 460, maxHeight: 380 } } }}
        >
          {fieldMenu?.field === 'audience' && audienceOptions.length === 0 && (
            <MenuItem disabled>No audiences resolved in this analysis</MenuItem>
          )}
          {fieldMenu?.field === 'audience' && audienceOptions.map((name) => (
            <MenuItem
              key={name}
              data-testid={AD_CREATIVE_INSIGHT.fieldOption('audience', name)}
              onClick={() => {
                setDerivedChips((current) => ({ ...current, audienceName: name }));
                setAudienceName('');
                setFieldMenu(null);
              }}
            >
              {name}
            </MenuItem>
          ))}
          {(fieldMenu?.field === 'pain' || fieldMenu?.field === 'job') && Object.entries(insightIndex)
            .filter(([, entry]) => (fieldMenu.field === 'pain'
              ? ['fears', 'habits', 'contexts'].includes(entry.category)
              : ['coreJobs', 'bigJobs', 'subtasks'].includes(entry.category)))
            .sort((a, b) => b[1].cumulativeWeight - a[1].cumulativeWeight)
            .slice(0, 10)
            .map(([insightId, entry]) => (
              <MenuItem
                key={insightId}
                data-testid={AD_CREATIVE_INSIGHT.fieldOption(fieldMenu.field, insightId)}
                onClick={() => {
                  if (fieldMenu.field === 'pain') {
                    setDerivedChips((current) => ({ ...current, painOrNeed: entry.thesis }));
                    setPainOrNeed('');
                  } else {
                    setDerivedChips((current) => ({ ...current, jobToBeDone: entry.thesis }));
                    setJobToBeDone('');
                  }
                  setFieldMenu(null);
                }}
                sx={{ whiteSpace: 'normal', alignItems: 'flex-start', gap: 1 }}
              >
                <Chip
                  size="small"
                  color={INSIGHT_CATEGORY_COLOR[entry.category] ?? 'default'}
                  label={entry.category}
                  sx={{ height: 18, fontSize: 10, mt: 0.25 }}
                />
                <Box>
                  <Typography variant="body2">{entry.thesis}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {`${entry.actorCount} actors · weight ${entry.cumulativeWeight}`}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
        </Menu>
        <Menu
          anchorEl={proofMenuAnchor}
          open={Boolean(proofMenuAnchor)}
          onClose={() => setProofMenuAnchor(null)}
          slotProps={{ paper: { sx: { maxWidth: 480, maxHeight: 360 } } }}
        >
          {proofOptions
            .filter((option) => !proofRefs.some((ref) => ref.id === option.id && ref.kind === option.kind))
            .map((option) => (
              <MenuItem
                key={`${option.kind}-${option.id}`}
                data-testid={AD_CREATIVE_INSIGHT.proofOption(option.id)}
                onClick={() => {
                  setProofRefs((current) => [...current, option]);
                  setProofMenuAnchor(null);
                }}
                sx={{ whiteSpace: 'normal' }}
              >
                <Typography variant="body2">
                  {`${option.kind === 'review' ? '💬' : '📈'} ${option.snippet}`}
                </Typography>
              </MenuItem>
            ))}
        </Menu>
        <Menu anchorEl={goalMenuAnchor} open={Boolean(goalMenuAnchor)} onClose={() => setGoalMenuAnchor(null)}>
          {GOAL_OPTIONS.map((option) => (
            <MenuItem
              key={option.id}
              selected={option.id === goal}
              onClick={() => {
                setGoal(option.id);
                setGoalMenuAnchor(null);
              }}
            >
              {option.label}
            </MenuItem>
          ))}
        </Menu>
        <Menu anchorEl={countMenuAnchor} open={Boolean(countMenuAnchor)} onClose={() => setCountMenuAnchor(null)}>
          {[1, 2, 3, 4, 5].map((count) => (
            <MenuItem
              key={count}
              selected={count === angleCount}
              onClick={() => {
                setAngleCount(count);
                setCountMenuAnchor(null);
              }}
            >
              {`${count} angle${count > 1 ? 's' : ''}`}
            </MenuItem>
          ))}
        </Menu>
        <Menu anchorEl={styleMenuAnchor} open={Boolean(styleMenuAnchor)} onClose={() => setStyleMenuAnchor(null)}>
          {designCollections.length === 0 && (
            <MenuItem disabled>No styles yet — auto style is used</MenuItem>
          )}
          {designCollections.map((collection) => (
            <MenuItem
              key={collection.id}
              selected={collection.id === styleId}
              onClick={() => {
                setStyleId(collection.id);
                setStyleMenuAnchor(null);
              }}
            >
              {collection.name || collection.id}
            </MenuItem>
          ))}
        </Menu>

        {/* FR4 (mock 03): network + full catalog grouped by family. */}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2, mb: 1 }}>
          <Chip
            size="small"
            data-testid={AD_CREATIVE_FORMATS.networkSelect}
            label={`Network: ${networks.find((network) => network.id === networkId)?.displayName ?? networkId} ▾`}
            onClick={(event) => setNetworkMenuAnchor(event.currentTarget)}
          />
          <Link
            component="button"
            variant="caption"
            data-testid={AD_CREATIVE_FORMATS.selectAll}
            onClick={() => setSelectedTemplateIds(catalogTemplates.map((template) => template.id))}
          >
            {`Select all ${catalogTemplates.length} formats`}
          </Link>
          <Box sx={{ flex: 1 }} />
          <GenerationSettingsPopover
            projectId={projectId}
            settings={genSettings.settings}
            onUpdate={genSettings.update}
            onReset={genSettings.reset}
          />
        </Stack>
        <Menu anchorEl={networkMenuAnchor} open={Boolean(networkMenuAnchor)} onClose={() => setNetworkMenuAnchor(null)}>
          {(networks.length ? networks : [{ id: 'meta', displayName: 'Meta Ads' }]).map((network) => {
            const empty = network.id !== 'meta' && network.id !== networkId;
            return (
              <MenuItem
                key={network.id}
                selected={network.id === networkId}
                data-testid={AD_CREATIVE_FORMATS.networkOption(network.id)}
                disabled={empty && network.id !== 'meta'}
                onClick={() => {
                  setNetworkId(network.id);
                  setNetworkMenuAnchor(null);
                }}
              >
                {`${network.displayName ?? network.id}${empty && network.id !== 'meta' ? ' · soon' : ''}`}
              </MenuItem>
            );
          })}
        </Menu>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
            gap: 1,
          }}
        >
          {templateFamilies.map(([family, familyTemplates]) => {
            const selectedInFamily = familyTemplates.filter((template) => selectedTemplateIds.includes(template.id)).length;
            const allSelected = selectedInFamily === familyTemplates.length;
            const recommended = recommendation.families.includes(family);
            return (
              <Box
                key={family}
                data-testid={AD_CREATIVE_FORMATS.family(family)}
                sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}
              >
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <Checkbox
                    size="small"
                    sx={{ p: 0.25 }}
                    data-testid={AD_CREATIVE_FORMATS.familyToggle(family)}
                    checked={allSelected}
                    indeterminate={selectedInFamily > 0 && !allSelected}
                    onChange={() => toggleFamily(family)}
                  />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, textTransform: 'capitalize' }}>
                    {family.replace(/_/g, ' ')}
                  </Typography>
                  {recommended && (
                    <Typography variant="caption" color="primary">
                      {`Recommended — ${recommendation.reason}`}
                    </Typography>
                  )}
                </Stack>
                {familyTemplates.map((template) => (
                  <Stack
                    key={template.id}
                    direction="row"
                    spacing={0.75}
                    alignItems="center"
                    data-testid={AD_CREATIVE_FORMATS.template(template.id)}
                    onClick={() => toggleTemplate(template.id)}
                    sx={{ pl: 2.5, py: 0.25, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                  >
                    <Checkbox
                      size="small"
                      sx={{ p: 0.25 }}
                      checked={selectedTemplateIds.includes(template.id)}
                      onChange={() => toggleTemplate(template.id)}
                      onClick={(event) => event.stopPropagation()}
                    />
                    <Typography variant="caption" sx={{ flex: 1, minWidth: 0 }} noWrap title={template.name}>
                      {template.name}
                    </Typography>
                    <Chip size="small" variant="outlined" label={templateMediaTag(template)} sx={{ height: 16, fontSize: 10 }} />
                  </Stack>
                ))}
                {/* FR7 (mock 05): framework affordance — proven frameworks for
                    this family, adapted to the selected insights. */}
                {(frameworksByFamily.get(family)?.length ?? 0) > 0 && (
                  <Stack
                    direction="row"
                    spacing={0.75}
                    alignItems="center"
                    flexWrap="wrap"
                    useFlexGap
                    data-testid={AD_CREATIVE_FRAMEWORKS.row(family)}
                    sx={{ pl: 2.5, pt: 0.5, mt: 0.5, borderTop: 1, borderColor: 'divider', borderTopStyle: 'dashed' }}
                  >
                    <Typography variant="caption" color="text.secondary">Framework:</Typography>
                    {frameworkIds
                      .filter((id) => frameworkById.get(id)?.formatFamilies.includes(family))
                      .map((id) => (
                        <Chip
                          key={id}
                          size="small"
                          color="primary"
                          variant="outlined"
                          data-testid={AD_CREATIVE_FRAMEWORKS.chip(id)}
                          label={frameworkById.get(id)?.name ?? id}
                          onDelete={() => setFrameworkIds((current) => current.filter((entry) => entry !== id))}
                          sx={{ height: 20, fontSize: 11 }}
                        />
                      ))}
                    <Link
                      component="button"
                      variant="caption"
                      data-testid={AD_CREATIVE_FRAMEWORKS.add(family)}
                      onClick={(event: React.MouseEvent<HTMLElement>) => setFrameworkMenu({ family, anchor: event.currentTarget })}
                    >
                      + add ▾
                    </Link>
                  </Stack>
                )}
              </Box>
            );
          })}
        </Box>
        {/* FR7: shared framework picker menu (per family, sorted by fit to the
            current insight selection; ≤2 per kind). */}
        <Menu
          anchorEl={frameworkMenu?.anchor ?? null}
          open={Boolean(frameworkMenu)}
          onClose={() => setFrameworkMenu(null)}
          slotProps={{ paper: { sx: { maxWidth: 460, maxHeight: 420 } } }}
        >
          {(frameworkMenu ? frameworksByFamily.get(frameworkMenu.family) ?? [] : []).map((framework) => (
            <MenuItem
              key={framework.id}
              data-testid={AD_CREATIVE_FRAMEWORKS.option(framework.id)}
              selected={frameworkIds.includes(framework.id)}
              onClick={() => toggleFramework(framework.id)}
              sx={{ alignItems: 'flex-start', whiteSpace: 'normal', py: 0.75 }}
            >
              <Checkbox size="small" checked={frameworkIds.includes(framework.id)} sx={{ p: 0.25, mr: 0.5, mt: -0.25 }} />
              <Box sx={{ minWidth: 0 }}>
                <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{framework.name}</Typography>
                  <Chip size="small" variant="outlined" label={framework.funnelStage.join('·')} sx={{ height: 16, fontSize: 10 }} />
                  {Object.entries(framework.insightAffinities ?? {}).map(([category, weight]) => (
                    <Chip
                      key={category}
                      size="small"
                      color={selectedCategories.has(category) ? (INSIGHT_CATEGORY_COLOR[category] ?? 'default') : 'default'}
                      variant={selectedCategories.has(category) ? 'filled' : 'outlined'}
                      label={`${category} ×${weight}`}
                      sx={{ height: 16, fontSize: 10, opacity: selectedCategories.has(category) ? 1 : 0.55 }}
                    />
                  ))}
                </Stack>
                <Typography variant="caption" color="text.secondary" title={framework.designNotes}>
                  {`${framework.gist}${framework.lengthSec ? ` · ~${framework.lengthSec}s` : ''}${framework.skeleton ? ` · ${framework.skeleton}` : ''}`}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Menu>
        {/* FR7: «Своё описание» — the peer alternative of a framework pick. */}
        <Box sx={{ mt: 1, border: 1, borderColor: 'divider', borderStyle: 'dashed', borderRadius: 1, p: 1 }}>
          <StyledTextField
            label="✍ Custom creative description (instead of a framework)"
            size="small"
            fullWidth
            multiline
            minRows={1}
            value={customBrief}
            data-testid={AD_CREATIVE_FRAMEWORKS.customField}
            onChange={(value) => {
              setCustomBrief(String(value));
              // Explicit peer choice: typing a description clears framework picks.
              if (String(value).trim()) setFrameworkIds([]);
            }}
          />
          {customBrief.trim() && (
            <Typography variant="caption" color="warning.main" data-testid={AD_CREATIVE_FRAMEWORKS.customExclusive}>
              Custom description replaces framework picks — selecting a framework clears this text.
            </Typography>
          )}
        </Box>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mt: 1 }}
          data-testid={AD_CREATIVE_FORMATS.matrixLine}
        >
          {`${angleCount} angle${angleCount > 1 ? 's' : ''} × ${selectedTemplateIds.length} format${selectedTemplateIds.length === 1 ? '' : 's'} = ${totalCreatives} creatives · ≈ $${estimatedCost} (estimated) · videos on demand`}
        </Typography>

        <Accordion disableGutters sx={{ mt: 2 }} data-testid={HOME.customizeAccordion}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">Customize brief (optional)</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5 }}>
              {/* D3 fix: fields SHOW the effective (derived-or-manual) values;
                  typing writes the manual state and flips the provenance dot. */}
              <StyledTextField
                label="Audience"
                size="small"
                value={effectiveAudience}
                data-testid={AD_CREATIVE_INSIGHT.briefField('audience')}
                onChange={(value) => setAudienceName(String(value))}
                endAdornment={grounded ? (
                  <Link
                    component="button"
                    variant="caption"
                    data-testid={AD_CREATIVE_INSIGHT.fieldFromInsights('audience')}
                    onClick={(event: React.MouseEvent<HTMLElement>) => setFieldMenu({ field: 'audience', anchor: event.currentTarget })}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    from insights ▾
                  </Link>
                ) : undefined}
              />
              <StyledTextField
                label="Pain / need"
                size="small"
                value={effectivePain}
                data-testid={AD_CREATIVE_INSIGHT.briefField('pain')}
                onChange={(value) => setPainOrNeed(String(value))}
                endAdornment={grounded ? (
                  <Link
                    component="button"
                    variant="caption"
                    data-testid={AD_CREATIVE_INSIGHT.fieldFromInsights('pain')}
                    onClick={(event: React.MouseEvent<HTMLElement>) => setFieldMenu({ field: 'pain', anchor: event.currentTarget })}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    from insights ▾
                  </Link>
                ) : undefined}
              />
              <StyledTextField
                label="Job to be done"
                size="small"
                value={effectiveJob}
                data-testid={AD_CREATIVE_INSIGHT.briefField('job')}
                onChange={(value) => setJobToBeDone(String(value))}
                endAdornment={grounded ? (
                  <Link
                    component="button"
                    variant="caption"
                    data-testid={AD_CREATIVE_INSIGHT.fieldFromInsights('job')}
                    onClick={(event: React.MouseEvent<HTMLElement>) => setFieldMenu({ field: 'job', anchor: event.currentTarget })}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    from insights ▾
                  </Link>
                ) : undefined}
              />
              <StyledTextField label="Offer promise" size="small" value={offerPromise} onChange={(value) => setOfferPromise(String(value))} />
              <StyledTextField label="Proof (source-backed only)" size="small" value={proofSummary} onChange={(value) => setProofSummary(String(value))} />
              <StyledTextField label="CTA" size="small" value={cta} onChange={(value) => setCta(String(value))} />
              <StyledTextField label="Landing URL" size="small" value={destinationUrl} onChange={(value) => setDestinationUrl(String(value))} />
              <StyledTextField
                label="Instruction for the AI"
                size="small"
                multiline
                minRows={2}
                sx={{ gridColumn: '1 / -1' }}
                value={instruction}
                onChange={(value) => setInstruction(String(value))}
              />
              {/* Proof sources (mock 02): picked ids -> sourceRefs on generate. */}
              {grounded && (
                <Stack
                  direction="row"
                  spacing={0.75}
                  flexWrap="wrap"
                  useFlexGap
                  alignItems="center"
                  data-testid={AD_CREATIVE_INSIGHT.proofRow}
                  sx={{ gridColumn: '1 / -1' }}
                >
                  <Typography variant="caption" color="text.secondary">Proof sources:</Typography>
                  {proofRefs.map((ref) => (
                    <Chip
                      key={`${ref.kind}-${ref.id}`}
                      size="small"
                      variant="outlined"
                      data-testid={AD_CREATIVE_INSIGHT.proofChip(ref.id)}
                      label={`${ref.kind === 'review' ? '💬' : '📈'} ${ref.snippet.length > 46 ? `${ref.snippet.slice(0, 46)}…` : ref.snippet}`}
                      onDelete={() => setProofRefs((current) => current.filter((entry) => !(entry.id === ref.id && entry.kind === ref.kind)))}
                    />
                  ))}
                  <Chip
                    size="small"
                    data-testid={AD_CREATIVE_INSIGHT.proofAdd}
                    label="+ Add proof source ▾"
                    onClick={(event) => setProofMenuAnchor(event.currentTarget)}
                    disabled={proofOptions.length === 0}
                  />
                </Stack>
              )}
            </Box>
          </AccordionDetails>
        </Accordion>

        {error && <Alert severity="error" sx={{ mt: 1.5 }} onClose={() => setError(null)}>{error}</Alert>}

        <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
          <Link component="button" variant="caption" data-testid={HOME.link('brand')} onClick={() => onOpenView('brand')}>
            Brand & Styles
          </Link>
          <Link component="button" variant="caption" data-testid={HOME.link('library')} onClick={() => onOpenView('library')}>
            Library
          </Link>
          <Link component="button" variant="caption" data-testid={HOME.link('import')} onClick={() => onOpenView('import')}>
            {`↻ ${providerSourceCount && providerSourceCount > 0 ? 'Sync updates' : 'Sync existing Meta creatives'}`}
          </Link>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto !important' }}>
            {metaConnected == null
              ? ''
              : `${metaConnected ? 'Meta: connected' : 'Meta: not connected'}${providerSourceCount != null ? ` · ${providerSourceCount >= 100 ? 'at least 100' : providerSourceCount} source records available` : ''}`}
          </Typography>
        </Stack>
      </Box>

      {recentBriefs.length > 0 && (
        <Box sx={{ maxWidth: 860, mx: 'auto', width: '100%' }} data-testid={HOME.recentBatches}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Recent batches</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 1 }}>
            {recentBriefs.map((brief) => {
              const briefItems = items.filter((item) => item.briefId === brief.id);
              const generating = briefItems.some((item) => {
                const status = deriveCardStatus(item);
                return status === 'generating' || status === 'video_generating';
              });
              const launched = briefItems.some((item) => Boolean(item.providerAdId));
              const state = launched ? 'launched' : generating ? 'generating' : briefItems.length ? 'review' : 'draft';
              return (
                <Box
                  key={brief.id}
                  data-testid={HOME.recentBatch(brief.id)}
                  onClick={() => onOpenBatch(brief.id)}
                  sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5, cursor: 'pointer', '&:hover': { borderColor: 'primary.light' } }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>{brief.title}</Typography>
                  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.5 }}>
                    <Chip
                      size="small"
                      variant="outlined"
                      color={state === 'launched' ? 'success' : state === 'generating' ? 'info' : 'default'}
                      label={state}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {`${briefItems.length} creatives`}
                    </Typography>
                    {getBriefCampaignId(brief) && <Typography variant="caption" color="text.secondary">· campaign linked</Typography>}
                  </Stack>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </Stack>
  );
}
